/**
 * The survey tree kept on the device, and how queued changes and sync results
 * are applied to it. Pure functions with no storage or network, so they can be
 * unit-tested.
 *
 * Shapes:
 * - A *snapshot* is the server's survey tree as returned by GET /surveys (every
 *   record nested under its parent), updated with each sync result.
 * - An *operation* is one queued change, in the format POST /api/sync takes:
 *   { op_id, op: 'create'|'update'|'delete', type, client_id,
 *     parent_client_id?, base_version?, data? }.
 * - The *view* is snapshot + queued operations: what the user sees.
 *
 * Records not yet on the server use their client_id as `id` and carry
 * `_pending: true`.
 */

export const RECORD_TYPES = {
  survey: { parent: null, fk: null, list: null, children: ['transects'], path: '/surveys' },
  transect: { parent: 'survey', fk: 'survey_id', list: 'transects', children: ['quadrats'], path: '/transects' },
  quadrat: {
    parent: 'transect',
    fk: 'transect_id',
    list: 'quadrats',
    children: ['mature_trees', 'regeneration_plots', 'disturbances'],
    path: '/quadrats',
  },
  mature_tree: { parent: 'quadrat', fk: 'quadrat_id', list: 'mature_trees', children: [], path: '/mature-trees', species: true },
  regeneration_plot: {
    parent: 'quadrat',
    fk: 'quadrat_id',
    list: 'regeneration_plots',
    children: ['regeneration_counts'],
    path: '/regeneration-plots',
  },
  regeneration_count: {
    parent: 'regeneration_plot',
    fk: 'regeneration_plot_id',
    list: 'regeneration_counts',
    children: [],
    path: '/regeneration-counts',
    species: true,
  },
  disturbance: { parent: 'quadrat', fk: 'quadrat_id', list: 'disturbances', children: [], path: '/disturbances' },
};

const TYPE_FOR_LIST = Object.fromEntries(
  Object.entries(RECORD_TYPES).filter(([, t]) => t.list).map(([type, t]) => [t.list, type])
);

// Fields that describe a record's place or history rather than its content.
const NOT_DATA = new Set(['id', 'client_id', 'version', 'created_at', 'updated_at', 'creator_id', 'created_by',
  'creator', 'owner_name', 'access', 'share_count', 'species_name', '_pending', '_queuedAt', 'survey', 'transect', 'quadrat', 'regeneration_plot']);

export const isClientId = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value));

/** Just the fields a client may send for `type` (no ids, parents or nested records). */
export const recordData = (type, payload) => {
  const { fk, children } = RECORD_TYPES[type];
  return Object.fromEntries(
    Object.entries(payload || {}).filter(([key]) => !NOT_DATA.has(key) && key !== fk && !children.includes(key))
  );
};

export const clone = (value) => (value === undefined ? value : JSON.parse(JSON.stringify(value)));

/**
 * Every record in the tree by type, looked up by server id or client_id:
 * index[type].get('12') or index[type].get('<uuid>') -> { node, parent, container }.
 */
export const buildIndex = (surveys) => {
  const index = Object.fromEntries(Object.keys(RECORD_TYPES).map((type) => [type, new Map()]));
  const add = (type, node, parent, container) => {
    const entry = { node, parent, container };
    index[type].set(String(node.id), entry);
    if (node.client_id) index[type].set(String(node.client_id), entry);
    RECORD_TYPES[type].children.forEach((list) => (node[list] || []).forEach((child) => add(TYPE_FOR_LIST[list], child, node, node[list])));
  };
  (surveys || []).forEach((survey) => add('survey', survey, null, surveys));
  return index;
};

export const findRecord = (surveys, type, ref, index = buildIndex(surveys)) =>
  ref === undefined || ref === null ? undefined : index[type].get(String(ref));

const withSpeciesName = (type, node, speciesName) => {
  if (RECORD_TYPES[type].species && speciesName && node.species !== undefined) {
    node.species_name = speciesName(node.species) ?? node.species_name ?? null;
  }
  return node;
};

const remove = (entry) => {
  const at = entry.container.indexOf(entry.node);
  if (at >= 0) entry.container.splice(at, 1);
};

/**
 * Apply one queued operation to `surveys` in place, keeping `index` current.
 * Operations whose target or parent no longer exists are skipped.
 */
const applyInPlace = (surveys, index, op, ctx = {}) => {
  const config = RECORD_TYPES[op.type];
  if (!config) return;
  const existing = index[op.type].get(String(op.client_id));

  if (op.op === 'create') {
    if (existing) {
      // Already in the snapshot (e.g. synced while this copy was being read).
      Object.assign(existing.node, op.data);
      return;
    }
    let container = surveys;
    let parentNode = null;
    if (config.parent) {
      const parent = index[config.parent].get(String(op.parent_client_id));
      if (!parent) return;
      parentNode = parent.node;
      parentNode[config.list] = parentNode[config.list] || [];
      container = parentNode[config.list];
    }
    const node = { ...op.data, id: op.client_id, client_id: op.client_id, version: 1, _pending: true, _queuedAt: op.queuedAt };
    if (config.fk) node[config.fk] = parentNode.id;
    config.children.forEach((list) => {
      node[list] = [];
    });
    withSpeciesName(op.type, node, ctx.speciesName);
    container.push(node);
    const entry = { node, parent: parentNode, container };
    index[op.type].set(String(node.id), entry);
    return;
  }

  if (!existing) return;
  if (op.op === 'update') {
    Object.assign(existing.node, op.data, { version: (existing.node.version || 0) + 1, _pending: true });
    withSpeciesName(op.type, existing.node, ctx.speciesName);
  } else if (op.op === 'delete') {
    remove(existing);
    index[op.type].delete(String(existing.node.id));
    if (existing.node.client_id) index[op.type].delete(String(existing.node.client_id));
  }
};

/** The view: a copy of `snapshot` with `operations` applied in order. */
export const applyOperations = (snapshot, operations, ctx = {}) => {
  const surveys = clone(snapshot || []);
  const index = buildIndex(surveys);
  operations.forEach((op) => applyInPlace(surveys, index, op, ctx));
  return surveys;
};

/**
 * A copy of `snapshot` updated with the server's answer to `op`, so the
 * change stays visible once the operation leaves the outbox.
 */
export const applyServerResult = (snapshot, op, result) => {
  const surveys = clone(snapshot || []);
  const index = buildIndex(surveys);
  const config = RECORD_TYPES[op.type];
  const entry = index[op.type]?.get(String(op.client_id));
  const { status, record } = result;

  const placeNew = () => {
    let container = surveys;
    if (config.parent) {
      const parent = index[config.parent].get(String(op.parent_client_id));
      if (!parent) return;
      parent.node[config.list] = parent.node[config.list] || [];
      container = parent.node[config.list];
    }
    const node = { ...record };
    config.children.forEach((list) => {
      node[list] = [];
    });
    container.push(node);
  };

  if ((status === 'applied' || status === 'duplicate') && op.op !== 'delete' && record) {
    if (entry) {
      Object.assign(entry.node, record);
      delete entry.node._pending;
      delete entry.node._queuedAt;
    } else if (op.op === 'create') {
      placeNew();
    }
  } else if (status === 'applied' && op.op === 'delete') {
    if (entry) remove(entry);
  } else if (status === 'conflict' && record) {
    if (entry) Object.assign(entry.node, record);
  } else if (status === 'not_found') {
    if (entry) remove(entry);
  }
  return surveys;
};

const LABELS = {
  survey: 'Survey',
  transect: 'Transect',
  quadrat: 'Quadrat',
  mature_tree: 'Tree',
  regeneration_plot: 'Regeneration plot',
  regeneration_count: 'Regeneration count',
  disturbance: 'Disturbance',
};

/** "Tree (Avicennia alba) · Quadrat 2 · Transect 1 · Pichavaram" for messages. */
export const describeRecord = (view, op) => {
  const index = buildIndex(view);
  const parts = [];
  let type = op.type;
  let entry = index[type]?.get(String(op.client_id));
  let label = LABELS[type] || type;
  const node = entry?.node || op.data || {};
  if (type === 'mature_tree' || type === 'regeneration_count') label += node.species_name ? ` (${node.species_name})` : '';
  if (type === 'transect' && node.transect_number) label += ` ${node.transect_number}`;
  if (type === 'quadrat' && node.quadrat_number) label += ` ${node.quadrat_number}`;
  if (type === 'regeneration_plot' && node.plot_number) label += ` ${node.plot_number}`;
  if (type === 'survey' && node.location) label += ` · ${node.location}`;
  parts.push(label);
  // Walk up the parents for context.
  let parentType = RECORD_TYPES[type]?.parent;
  let parentRef = entry ? entry.parent?.id : op.parent_client_id;
  while (parentType) {
    const parent = index[parentType].get(String(parentRef));
    if (!parent) break;
    const p = parent.node;
    if (parentType === 'survey') parts.push(p.location || 'Survey');
    if (parentType === 'transect') parts.push(`Transect ${p.transect_number}`);
    if (parentType === 'quadrat') parts.push(`Quadrat ${p.quadrat_number}`);
    if (parentType === 'regeneration_plot') parts.push(`Plot ${p.plot_number}`);
    type = parentType;
    parentType = RECORD_TYPES[type].parent;
    parentRef = parent.parent?.id;
  }
  return parts.join(' · ');
};

const OK = new Set(['applied', 'duplicate']);

const MESSAGES = {
  conflict: 'Someone else changed this on the server, so your change was not applied. The server version is shown now.',
  not_found: 'This had been deleted on the server, so your change was not applied.',
  rejected: 'The server could not accept this change.',
  forbidden: "Your role doesn't allow this change.",
  blocked: 'Not sent, because an earlier change it depends on was not applied.',
};

/**
 * Work out what a sync reply means for the device.
 *
 * `sent` are the operations sent and `results` the server's answers, in the
 * same order; `waiting` are operations still in the outbox after them. Returns
 * the new snapshot, the outbox seqs to remove, and problems to show the user.
 *
 * Later operations that depend on a failed one are dropped as "blocked":
 * - after any failure, later changes to the same record (after a conflict,
 *   sending them would overwrite someone else's change);
 * - after a refused create or a record deleted on the server, anything that
 *   would go under it too (it doesn't exist on the server).
 */
export const reconcile = ({ snapshot, view, sent, results, waiting = [] }) => {
  let next = snapshot;
  const removeSeqs = [];
  const problems = [];
  const blockedRecords = new Set(); // no further changes to these
  const missingParents = new Set(); // nothing can be created under these

  sent.forEach((op, i) => {
    const result = results[i] || { status: 'rejected', message: 'No reply for this change' };
    next = applyServerResult(next, op, result);
    removeSeqs.push(op.seq);
    if (!OK.has(result.status)) {
      problems.push({
        status: result.status,
        op: op.op,
        type: op.type,
        clientId: op.client_id,
        what: describeRecord(view, op),
        message: result.message && result.status === 'rejected' ? result.message : MESSAGES[result.status] || result.message,
        errors: result.errors || null,
        data: op.data || null,
      });
      blockedRecords.add(String(op.client_id));
      if (op.op === 'create' || result.status === 'not_found') missingParents.add(String(op.client_id));
    }
  });

  if (blockedRecords.size) {
    waiting.forEach((op) => {
      const sameRecord = blockedRecords.has(String(op.client_id));
      const underMissing = missingParents.has(String(op.parent_client_id));
      if (!sameRecord && !underMissing) return;
      // Whatever it would have created is missing in turn.
      blockedRecords.add(String(op.client_id));
      if (op.op === 'create') missingParents.add(String(op.client_id));
      removeSeqs.push(op.seq);
      problems.push({
        status: 'blocked',
        op: op.op,
        type: op.type,
        clientId: op.client_id,
        what: describeRecord(view, op),
        message: MESSAGES.blocked,
        errors: null,
        data: op.data || null,
      });
    });
  }

  return { snapshot: next, removeSeqs, problems };
};
